import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LocalidadBasicDto } from '@models/localidad/localidadBasicDto.model';
import { GeorefService } from '@services/georef/georef.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, of, startWith, debounceTime, map } from 'rxjs';

@Component({
  selector: 'app-buscar-localidad-georef',
  standalone: true,
  imports: [
    MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatButtonModule,
    MatIconModule, MatTooltipModule, ReactiveFormsModule, NgxSpinnerModule, CommonModule
  ],
  templateUrl: './buscar-localidad-georef.component.html',
  styleUrl: './buscar-localidad-georef.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuscarLocalidadGeorefComponent implements OnChanges {

  @Input() idLocalidad?: any;
  @Input() provincia: any;
  @Output() localidadSeleccionada = new EventEmitter<any>();

  localidadCtrl = new FormControl();
  localidades: any[] = [];
  filteredLocalidades: Observable<any[]> = of([]);

  constructor(
    private georefService: GeorefService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
  ) {
    this.localidadCtrl.disable(); // por defecto deshabilitado
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.idLocalidad && !this.provincia) {
      this.localidadCtrl.disable();
      this.localidades = [];
      this.filteredLocalidades = of([]);
      return;
    }

    if (this.idLocalidad) {
      this.localidadCtrl.enable();
      if (this.provincia?.nombre) {
        const nombreProvincia = this.provincia?.nombre;
        this.loadLocalidades(nombreProvincia)
      }


    } else if (this.provincia?.nombre) {
      this.loadLocalidades(this.provincia.nombre);
    }
  }

  private loadLocalidades(nombreProvincia: string, callback?: () => void) {
    this.spinner.show("spLocalidad");
    this.georefService.getLocalidadesPorProvincia(nombreProvincia).subscribe({
      next: res => {
        this.spinner.hide("spLocalidad");
        this.localidades = res.localidades ?? [];

        if (this.localidades.length > 0) {
          this.localidadCtrl.enable();
          this.filteredLocalidades = this.localidadCtrl.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            map(value => typeof value === 'string' ? value : value?.nombre),
            map(nombre => this.filtrar(nombre))
          );
        } else {
          this.localidadCtrl.disable();
          this.filteredLocalidades = of([]);
        }

        if (this.idLocalidad) {
          const localidad = this.localidades.find(l => l.id === this.idLocalidad);
          if (localidad) {
            this.localidadCtrl.setValue(localidad);
            this.localidadSeleccionada.emit(localidad);
          }
        }

        this.localidadCtrl.enable(); // Habilitamos el control luego de cargar

        callback?.();
      },
      error: err => {
        this.spinner.hide("spLocalidades");
        this.snackBar.showError(err, "Error al obtener localidades");
      }
    });
  }

  filtrar(nombre: string): any[] {
    const val = nombre.toLowerCase();
    return this.localidades.filter(l => l.nombre.toLowerCase().includes(val));
  }

  displayFn(localidad: any): string {
    return localidad?.nombre ?? '';
  }

  onLocalidadSelected(localidad: any) {
    this.localidadSeleccionada.emit(localidad);
  }

  getLocalidadPorId(idLocalidad: number): void {
    this.spinner.show('spLocalidad');
    this.georefService.getLocalidadPorId(idLocalidad).subscribe({
      next: (data) => {
        this.spinner.hide('spLocalidad');
        if (data) {
          this.idLocalidad = data.id;
          this.provincia = data.provincia;
        }
      },
      error: (error) => {
        this.spinner.hide('spLocalidad');
        this.snackBar.showError(error, 'Error');
      }
    });
  }
}
